import {
  Bleed,
  BlockStack,
  Button,
  Card,
  Grid,
  Icon,
  InlineGrid,
  InlineStack,
  Spinner,
  Text,
  useBreakpoints,
} from '@shopify/polaris'
import { StarFilledIcon, XIcon } from '@shopify/polaris-icons'
import { useEffect, useState } from 'react'

import { Result } from './api'
import { anyWindow, ResultToast } from './helpers'
import styles from './NpsSurveyCard.module.css'
import { EligibleForReview, setNpsSurveyScore, useShopifyReview } from './ShopifyReviewHandler'
import { useUiProp } from './ui-props'

export const NpsSurveyCard = ({ trigger }: { trigger: string }) => {
  const { smUp } = useBreakpoints()
  const shopifyReviewRequest = useShopifyReview()
  const dismissedAtProp = useUiProp('nps_survey_card_dismissed_at')
  const lastDisplayedAtProp = useUiProp('nps_survey_card_last_displayed_at')
  const eligibleForReview = anyWindow.eligibleForReview as EligibleForReview

  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<Result<any> | null>(null)

  const handleSetNpsSurveyScore = async (score: number) => {
    setSubmitting(true)
    const res = await setNpsSurveyScore(score)
    setSubmitting(false)

    if (res.status === 'success') {
      anyWindow.eligibleForReview = { ...eligibleForReview, eligible: false }
    }

    if (res.status === 'success' && score >= 9) {
      shopifyReviewRequest.show(trigger)
    }

    setResult(res)
  }

  const handleDismiss = () => {
    dismissedAtProp.patch(new Date().toISOString())
  }

  const dismissedAt = dismissedAtProp.value ? Date.parse(dismissedAtProp.value) : null
  const dismissDays = 60
  const recentlyDismissed =
    !anyWindow.newUserTest &&
    dismissedAt &&
    dismissedAt > Date.now() - 1000 * 60 * 60 * 24 * dismissDays

  const shouldDisplay = Boolean(eligibleForReview?.eligible && !recentlyDismissed)

  useEffect(() => {
    if (shouldDisplay) {
      lastDisplayedAtProp.patch(new Date().toISOString())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldDisplay])

  return (
    <>
      <ResultToast result={result} setResult={setResult} />
      {shouldDisplay && (
        <div className={styles.npsWrapper}>
          <Card background="bg-surface-info-hover">
            <BlockStack gap="400">
              <InlineStack align="space-between" wrap={false} gap="200">
                <InlineStack gap="200" wrap={false}>
                  <Icon source={StarFilledIcon} />
                  <Text as="h2">
                    How likely are you to recommend PayWhirl to a friend or colleague?
                  </Text>
                </InlineStack>
                <Button variant="plain" icon={XIcon} size="micro" onClick={handleDismiss}></Button>
              </InlineStack>
              <InlineStack align="start">
                <div className={smUp ? styles.ratingBox : undefined}>
                  <InlineGrid
                    columns={{ xs: 6, sm: 12, md: 12, lg: 12, xl: 12 }}
                    gap="200"
                    alignItems="start"
                  >
                    {Array.from({ length: 11 }, (_, index) => (
                      <Button
                        size="micro"
                        key={index.toString()}
                        onClick={() => handleSetNpsSurveyScore(index)}
                        disabled={submitting}
                      >
                        &nbsp;{String(index)}&nbsp;
                      </Button>
                    ))}
                    <InlineStack align="center" blockAlign="center">
                      {submitting && <Spinner size="small" />}
                    </InlineStack>
                    <Grid.Cell columnSpan={{ xs: 3, sm: 6, md: 6, lg: 6, xl: 6 }}>
                      <Bleed marginInlineStart={smUp ? '300' : undefined}>
                        <Text as="span">Not likely</Text>
                      </Bleed>
                    </Grid.Cell>
                    <Grid.Cell columnSpan={{ xs: 3, sm: 5, md: 5, lg: 5, xl: 5 }}>
                      <InlineStack align="end">
                        <Bleed marginInlineEnd={smUp ? '300' : undefined}>
                          <Text as="span">Very likely</Text>
                        </Bleed>
                      </InlineStack>
                    </Grid.Cell>
                  </InlineGrid>
                </div>
              </InlineStack>
            </BlockStack>
          </Card>
        </div>
      )}
    </>
  )
}
